import { ChartBounds, ChartDimensions, AxisScale } from './types';

/**
 * SVG text のおおよその幅（指数表記・上付き文字を含む）。Canvas 計測なしで左余白を確保する用途。
 */
function estimateSvgTickLabelWidth(text: string, fontSize: number): number {
  let w = 0;
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if (code >= 0x2070 && code <= 0x209f) {
      w += fontSize * 0.38;
      continue;
    }
    if (ch === '×' || ch === '·' || ch === '⋅') {
      w += fontSize * 0.42;
    } else if ('0123456789.eE+-'.includes(ch)) {
      w += fontSize * 0.56;
    } else {
      w += fontSize * 0.58;
    }
  }
  return Math.max(w, fontSize * 1.25);
}

interface TextStyle {
  fontSize: number;
  fontWeight: 'normal' | 'bold' | '500' | '600' | '700';
  fontStyle: 'normal' | 'italic';
  fontFamily: string;
  fill: string;
}

interface KeisukeAxisProps {
  bounds: ChartBounds;
  dimensions: ChartDimensions;
  type: 'x' | 'y';
  scale: AxisScale;
  label?: string;
  showGrid?: boolean;
  tickFormatter?: (value: number) => string;
  gridLength?: number;
  /** Y軸の縦線のX座標（SVG）。X軸を短く中央寄せしたときは X軸左端と同じ値を指定し、左下で軸が交わるようにする */
  yAxisLineX?: number;
  // 交点の値（X軸の場合はY軸上の値、Y軸の場合はX軸上の値）
  intersectionValue?: number;
  // 交点の軸のドメイン（X軸の場合はY軸のドメイン、Y軸の場合はX軸のドメイン）
  intersectionDomain?: [number, number];
  // Y軸ラベルの位置（負の値で左方向）
  yAxisLabelPosition?: number;
  // X軸ラベルの位置（正の値で下方向）
  xAxisLabelPosition?: number;
  // グリッド線の設定
  gridLineStyle?: 'solid' | 'dashed' | 'dotted';
  gridOpacity?: number;
  gridColor?: string;
  // 軸線の太さ
  axisLineWidth?: number;
  // テキストスタイル設定
  axisLabelStyle?: TextStyle;
  tickLabelStyle?: TextStyle;
}

/**
 * KeisukeAxis - 軸描画コンポーネント
 */
export default function KeisukeAxis({
  bounds,
  dimensions,
  type,
  scale,
  label,
  showGrid = false,
  intersectionValue: propIntersectionValue,
  intersectionDomain,
  yAxisLabelPosition = -65,
  xAxisLabelPosition = 40,
  gridLineStyle = 'dashed',
  gridOpacity = 0.3,
  gridColor = '#e5e5e5',
  axisLineWidth = 3,
  axisLabelStyle,
  tickLabelStyle,
  tickFormatter,
  gridLength,
  yAxisLineX,
}: KeisukeAxisProps) {
  const { domain, range, ticks } = scale;
  const [domainMin, domainMax] = domain;
  const [rangeMin, rangeMax] = range;

  // スケール関数を作成
  const scaleFunc = (value: number) => {
    if (domainMax === domainMin) return rangeMin;
    const ratio = (value - domainMin) / (domainMax - domainMin);
    return rangeMin + ratio * (rangeMax - rangeMin);
  };

  /** 目盛り文字・軸線・目盛り短線の色（目盛り色が空なら軸タイトル色へ。一括「文字色」と整合） */
  const tickStrokeAndFill =
    tickLabelStyle?.fill && String(tickLabelStyle.fill).trim() !== ''
      ? tickLabelStyle.fill
      : axisLabelStyle?.fill && String(axisLabelStyle.fill).trim() !== ''
        ? axisLabelStyle.fill
        : '#333333';

  // 交点の位置を計算
  let axisY = bounds.y + bounds.height;
  let axisX = type === 'y' && yAxisLineX !== undefined ? yAxisLineX : bounds.x;

  if (type === 'x' && propIntersectionValue !== undefined && intersectionDomain) {
    // X軸の交点位置（Y軸上の値）を使用してX軸のY位置を決定
    const [intersectionMin, intersectionMax] = intersectionDomain;
    if (intersectionMax !== intersectionMin) {
      // 交点がドメイン範囲内にある場合のみ、その位置に描画
      if (propIntersectionValue >= intersectionMin && propIntersectionValue <= intersectionMax) {
        const intersectionRatio = (propIntersectionValue - intersectionMin) / (intersectionMax - intersectionMin);
        axisY = bounds.y + bounds.height - (intersectionRatio * bounds.height);
      } else if (propIntersectionValue < intersectionMin) {
        // ドメインの最小値より小さい場合は、下端に描画
        axisY = bounds.y + bounds.height;
      } else {
        // ドメインの最大値より大きい場合は、上端に描画
        axisY = bounds.y;
      }
    }
  } else if (type === 'y' && propIntersectionValue !== undefined && intersectionDomain) {
    // Y軸の交点位置（X軸上の値）を使用してY軸のX位置を決定
    const [intersectionMin, intersectionMax] = intersectionDomain;
    if (intersectionMax !== intersectionMin) {
      const yOriginX = yAxisLineX !== undefined ? yAxisLineX : bounds.x;
      const plotSpanX =
        yAxisLineX !== undefined
          ? gridLength ?? bounds.x + bounds.width - yOriginX
          : bounds.width;
      if (propIntersectionValue >= intersectionMin && propIntersectionValue <= intersectionMax) {
        const intersectionRatio =
          (propIntersectionValue - intersectionMin) / (intersectionMax - intersectionMin);
        axisX = yOriginX + intersectionRatio * plotSpanX;
      } else if (propIntersectionValue < intersectionMin) {
        axisX = yOriginX;
      } else {
        axisX = yOriginX + plotSpanX;
      }
    }
  }

  if (type === 'x') {
    // X軸
    const axisLength = bounds.width;

    return (
      <g>
        {/* グリッド線 */}
        {showGrid &&
          ticks.map((tick) => {
            const x = bounds.x + scaleFunc(tick);
            return (
              <line
                key={tick}
                x1={x}
                y1={bounds.y}
                x2={x}
                y2={bounds.y + bounds.height}
                stroke={gridColor}
                strokeWidth="0.5"
                strokeDasharray={
                  gridLineStyle === 'dashed' ? '4,4' :
                  gridLineStyle === 'dotted' ? '2,2' :
                  'none'
                }
                opacity={gridOpacity}
              />
            );
          })}

        {/* 軸線 */}
        <line
          x1={bounds.x}
          y1={axisY}
          x2={bounds.x + axisLength}
          y2={axisY}
          stroke={tickStrokeAndFill}
          strokeWidth={axisLineWidth}
        />
        {/* 交点がドメイン範囲内でない場合の補助線（必要に応じて） */}

        {/* 目盛りとラベル */}
        {ticks.map((tick) => {
          const x = bounds.x + scaleFunc(tick);
          const tickLength = 5;
          // 軸線の下端から目盛り線を開始（軸線に埋もれないようにする）
          const tickStartY = axisY + axisLineWidth / 2;
          const tickEndY = axisY + tickLength;
          const labelY = axisY + tickLength + 15;

          return (
            <g key={tick}>
              {/* 目盛り */}
              <line
                x1={x}
                y1={tickStartY}
                x2={x}
                y2={tickEndY}
                stroke={tickStrokeAndFill}
                strokeWidth={axisLineWidth}
              />
              {/* ラベル */}
              <text
                x={x}
                y={labelY}
                textAnchor="middle"
                fontSize={tickLabelStyle?.fontSize ?? 16}
                fontWeight={tickLabelStyle?.fontWeight ?? 'normal'}
                fontStyle={tickLabelStyle?.fontStyle ?? 'normal'}
                fontFamily={tickLabelStyle?.fontFamily ?? 'Arial, sans-serif'}
                fill={tickStrokeAndFill}
              >
                {tickFormatter
                  ? tickFormatter(tick)
                  : tick.toFixed(domainMax - domainMin < 1 ? 2 : 1)}
              </text>
            </g>
          );
        })}

        {/* 軸ラベル */}
        {label && (
          <text
            x={bounds.x + axisLength / 2}
            y={axisY + xAxisLabelPosition}
            textAnchor="middle"
            fontSize={axisLabelStyle?.fontSize ?? 16}
            fontWeight={axisLabelStyle?.fontWeight ?? '500'}
            fontStyle={axisLabelStyle?.fontStyle ?? 'normal'}
            fontFamily={axisLabelStyle?.fontFamily ?? 'Arial, sans-serif'}
            fill={axisLabelStyle?.fill ?? '#333333'}
          >
            {label}
          </text>
        )}
      </g>
    );
  } else {
    // Y軸
    const axisLength = bounds.height;
    const tickLength = 5;
    const tickFontSize = tickLabelStyle?.fontSize ?? 16;
    const axisLblFontSize = axisLabelStyle?.fontSize ?? 16;

    const tickStrings = ticks.map((tick) =>
      tickFormatter
        ? tickFormatter(tick)
        : tick.toFixed(domainMax - domainMin < 1 ? 2 : 1)
    );
    const maxTickLabelWidth =
      tickStrings.reduce(
        (m, s) => Math.max(m, estimateSvgTickLabelWidth(s, tickFontSize)),
        tickFontSize * 1.5
      ) + 16;

    /** 目盛り文字の右端（textAnchor=end の基準 X） */
    const tickTextRightX = axisX - tickLength - 8;
    /** 指数表記など幅広ラベルと、-90° 回転した軸タイトルが重ならないようタイトル中心 X を左へ逃がす */
    const axisLabelHorizHalf = axisLblFontSize * 0.72;
    const gapTickToTitle = 12;
    const maxAxisLabelCenterX =
      tickTextRightX - maxTickLabelWidth - gapTickToTitle - axisLabelHorizHalf;
    const requestedAxisLabelCenterX = axisX + yAxisLabelPosition;
    const axisLabelCenterX = Math.min(requestedAxisLabelCenterX, maxAxisLabelCenterX);
    const axisLabelCenterY = bounds.y + axisLength / 2;

    /** X軸を帯状に中央寄せしたときは Y軸位置から右へ。従来はプロット左端から幅いっぱい */
    const gridLineStartX = yAxisLineX !== undefined ? axisX : bounds.x;
    const gridLineEndX =
      yAxisLineX !== undefined
        ? axisX + (gridLength ?? bounds.x + bounds.width - axisX)
        : bounds.x + (gridLength ?? bounds.width);

    return (
      <g>
        {/* グリッド線 */}
        {showGrid &&
          ticks.map((tick) => {
            const y = bounds.y + bounds.height - scaleFunc(tick);
            return (
              <line
                key={tick}
                x1={gridLineStartX}
                y1={y}
                x2={gridLineEndX}
                y2={y}
                stroke={gridColor}
                strokeWidth="0.5"
                strokeDasharray={
                  gridLineStyle === 'dashed' ? '4,4' :
                  gridLineStyle === 'dotted' ? '2,2' :
                  'none'
                }
                opacity={gridOpacity}
              />
            );
          })}

        {/* 軸線 */}
        <line
          x1={axisX}
          y1={bounds.y}
          x2={axisX}
          y2={bounds.y + axisLength}
          stroke={tickStrokeAndFill}
          strokeWidth={axisLineWidth}
        />
        {/* 交点がドメイン範囲内でない場合の補助線（必要に応じて） */}

        {/* 目盛りとラベル */}
        {ticks.map((tick, tickIndex) => {
          const y = bounds.y + bounds.height - scaleFunc(tick);
          // 軸線の左端から目盛り線を開始（軸線に埋もれないようにする）
          const tickStartX = axisX - axisLineWidth / 2;
          const tickEndX = axisX - tickLength - axisLineWidth / 2;

          return (
            <g key={`${tick}-${tickIndex}`}>
              {/* 目盛り */}
              <line
                x1={tickStartX}
                y1={y}
                x2={tickEndX}
                y2={y}
                stroke={tickStrokeAndFill}
                strokeWidth={axisLineWidth}
              />
              {/* ラベル */}
              <text
                x={tickTextRightX}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={tickFontSize}
                fontWeight={tickLabelStyle?.fontWeight ?? 'normal'}
                fontStyle={tickLabelStyle?.fontStyle ?? 'normal'}
                fontFamily={tickLabelStyle?.fontFamily ?? 'Arial, sans-serif'}
                fill={tickStrokeAndFill}
              >
                {tickStrings[tickIndex]}
              </text>
            </g>
          );
        })}

        {/* 軸ラベル */}
        {label && (
          <text
            x={axisLabelCenterX}
            y={axisLabelCenterY}
            textAnchor="middle"
            fontSize={axisLblFontSize}
            fontWeight={axisLabelStyle?.fontWeight ?? '500'}
            fontStyle={axisLabelStyle?.fontStyle ?? 'normal'}
            fontFamily={axisLabelStyle?.fontFamily ?? 'Arial, sans-serif'}
            fill={axisLabelStyle?.fill ?? '#333333'}
            transform={`rotate(-90, ${axisLabelCenterX}, ${axisLabelCenterY})`}
          >
            {label}
          </text>
        )}
      </g>
    );
  }
}

